import React, { useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { clearAuth } from '@/lib/auth';
import { CommandRail } from './command/CommandRail';
import { CommandOverviewPage } from './command/CommandOverviewPage';
import { CommandBlueprintsPage } from './command/CommandBlueprintsPage';
import { CommandPendingPage } from './command/CommandPendingPage';
import { CommandLegacyPage, LEGACY_COMMANDS } from './command/CommandLegacyPage';

// Compatibility: a legacy `?mode=` query link resolves to its canonical route.
// A legacy mode keeps its own path; the blueprint-flagged modes resolve to the
// preserved blueprint content on /command/blueprints. A mode that names none of
// the known destinations is left alone rather than guessed at.
function resolveQueryMode(raw: string | null): string | null {
    if (!raw) return null;
    const mode = raw.trim().toLowerCase();
    if (!/^[a-z0-9-]+$/.test(mode)) return null;
    if (mode === 'overview') return '/command';
    const legacy = LEGACY_COMMANDS.find((entry) => entry.mode === mode);
    return legacy ? legacy.canonical : `/command/${mode}`;
}

// The Command Center.
//
// The tab bar became a Command-local rail and every tab became a route:
// /command, /command/allowance, /command/models, /command/pipeline,
// /command/decisions, /command/blueprints and the legacy /command/<mode>
// panels. The shell no longer clips itself to a fixed viewport, so the page
// scrolls normally on a phone.
export const CommandCenter: React.FC = () => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryTarget = resolveQueryMode(searchParams.get('mode'));

    const handleLogout = () => {
        clearAuth();
        navigate('/login', { replace: true });
    };

    return (
        <div className="w-full min-h-screen bg-[#0a0a0a] text-gray-200 p-4 font-mono">
            {/* Header */}
            <header className="mb-4 flex flex-col gap-3 border-b border-gray-800 pb-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 className="text-xl font-bold tracking-[0.2em] text-white">DIRECTOR<span className="text-[#39FF14]">::</span>COMMAND</h1>
                    <span className="text-xs text-gray-500">SEITIATE+ OPERATOR COCKPIT // PRE-ORCHESTRA RECOVERY</span>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <div className="text-[#39FF14] text-xs animate-pulse">● SYSTEM ONLINE</div>
                        <div className="text-xs text-gray-600">SESSION: ID-AUTH-MASTER</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSettingsOpen(true)}
                        className="p-2 text-gray-500 hover:text-[#39FF14] border border-gray-800 hover:border-[#39FF14]/30 rounded transition-colors"
                        title="Settings"
                        aria-label="Settings"
                    >
                        <Settings className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-gray-500 hover:text-red-400 border border-gray-800 hover:border-red-400/50 rounded transition-colors"
                    >
                        Logout
                    </button>
                </div>
            </header>

            {/* Rail + routed page */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                <nav aria-label="Command sections" className="lg:sticky lg:top-4 lg:w-56 lg:shrink-0">
                    <CommandRail />
                </nav>
                <div className="min-w-0 flex-1">
                    {queryTarget ? (
                        <Navigate to={queryTarget} replace />
                    ) : (
                        <Routes>
                            <Route index element={<CommandOverviewPage />} />
                            <Route path="allowance" element={<CommandPendingPage variant="allowance" />} />
                            <Route path="models" element={<CommandPendingPage variant="models" />} />
                            <Route path="pipeline" element={<CommandPendingPage variant="pipeline" />} />
                            <Route path="decisions" element={<CommandPendingPage variant="decisions" />} />
                            <Route path="blueprints" element={<CommandBlueprintsPage />} />
                            <Route path=":mode" element={<CommandLegacyPage />} />
                            <Route path="*" element={<Navigate to="/command" replace />} />
                        </Routes>
                    )}
                </div>
            </div>

            {/* Settings Modal Overlay */}
            {settingsOpen && (
                <SettingsModal
                    mode="admin"
                    onClose={() => setSettingsOpen(false)}
                />
            )}
        </div>
    );
};
